import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FunnelIcon,
  MapPinIcon,
  FaceFrownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { 
  fetchExploreUsers, 
  fetchFilterOptions, 
  setFilters, 
  clearFilters,
  saveFilterPreset,
  fetchFilterPresets,
  fetchFilterHistory,
  replaceFilters
} from '../store/slices/exploreSlice';
import { ExploreFilters, User, FilterPreset } from '../types';
import Button from '../components/common/Button';
import Logo from '../components/common/Logo';
import toast from 'react-hot-toast';
import { locationAPI, userRelationshipAPI } from '../services/api';
import { useLocation } from '../hooks/useLocation';
import CollaborationRequestModal from '../components/collaboration/CollaborationRequestModal';
import { updateUser } from '../store/slices/authSlice';
import { useRestrictions } from '../hooks/useRestrictions';
// [Subscription removed] UpgradePopup removed — platform is fully free.
// import UpgradePopup from '../components/common/UpgradePopup';

// New Components
import ExploreHeader from '../components/explore/ExploreHeader';
import FilterModal from '../components/explore/FilterModal';
import FilterBar from '../components/explore/FilterBar';
import UserCard from '../components/explore/UserCard';
import SEOHead from '../components/common/SEOHead';
import ModelQuickViewModal from '../components/explore/ModelQuickViewModal';
import LoginPromptModal from '../components/common/LoginPromptModal';
import GuestHeader from '../components/layout/GuestHeader';

// SEO Data
import { getCityBySlug, DEFAULT_SEO_RADIUS, CityInfo } from '../data/cityData';
import { getCategoryBySlug, CategoryInfo } from '../data/categoryData';

const ExplorePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // [Subscription removed] upgradeConfig state and checkRestriction removed — platform is fully free.
  // const { checkRestriction } = useRestrictions();
  
  // SEO URL Params
  const { city: citySlug, category: categorySlug } = useParams<{ city?: string; category?: string }>();
  const seoCity = citySlug ? getCityBySlug(citySlug) : undefined;
  const seoCategory = categorySlug ? getCategoryBySlug(categorySlug) : undefined;
  
  // Redux State
  const { 
    users, 
    loading, 
    filters, 
    pagination, 
    hasMore,
    filterPresets,
    filterOptions
  } = useAppSelector((state) => state.explore);
  const { user: currentUser, isAuthenticated } = useAppSelector((state) => state.auth);

  // Filter out users with no photos, or disabled/deleted
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      if ((u as any).is_disabled || (u as any).deletion_scheduled_at || (u as any).deleted_at) {
        return false;
      }
      const images = u.recent_images_signed || [];
      return images.some(img => img && img.trim() !== '');
    });
  }, [users]);

  // Local State
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [nearbyLoading, setNearbyLoading] = useState(false);
  
  // Collaboration Modal State
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);
  const [selectedUserForCollab, setSelectedUserForCollab] = useState<User | null>(null);

  // Model Quick View Modal State
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [selectedUserForQuickView, setSelectedUserForQuickView] = useState<User | null>(null);

  // Follow State
  const [followMap, setFollowMap] = useState<Record<string, boolean>>({});

  // Login Prompt Modal State (for guests)
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMessage, setLoginModalMessage] = useState('');
  const [followLoading, setFollowLoading] = useState(false);

  const { getCurrentLocation } = useLocation();

  // Infinite Scroll Refs
  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Infinite Scroll Observer
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { rootMargin: '800px' }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loading, pagination, filters, dispatch]);

  // Initialize Filters from URL
  const searchParamFilters = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);

  // SEO URL Filter Initialization
  useEffect(() => {
    // Check if we have SEO URL params (city/category)
    if (seoCity || seoCategory) {
      const seoFilters: ExploreFilters = {
        page: 1,
        limit: 20,
        sort_by: 'smart_sort',
        sort_order: 'desc',
      };

      // Apply city-based location filter
      if (seoCity) {
        seoFilters.latitude = seoCity.lat;
        seoFilters.longitude = seoCity.lng;
        seoFilters.radius = DEFAULT_SEO_RADIUS; // 50km default
        seoFilters.location = seoCity.name;
      }

      // Apply category-based filters
      if (seoCategory) {
        seoFilters.specializations = seoCategory.specializationIds;
        if (seoCategory.userType) {
          seoFilters.user_type = seoCategory.userType;
        }
      }

      dispatch(replaceFilters(seoFilters));
      dispatch(fetchExploreUsers({ filters: seoFilters }));
      return; // Skip other filter initializations
    }

    // Regular search params initialization
    if (Object.keys(searchParamFilters).length > 0) {
        const defaultFilters: ExploreFilters = {
          page: 1,
          limit: 20,
          sort_by: 'smart_sort',
          sort_order: 'desc',
        };

        const newFilters: any = { ...defaultFilters };
        if (searchParamFilters.user_type) newFilters.user_type = searchParamFilters.user_type;
        if (searchParamFilters.specialization) newFilters.specialization = searchParamFilters.specialization;
        const searchVal = searchParamFilters.search || searchParamFilters.q;
        if (searchVal) {
          newFilters.search = searchVal;
          setSearchInput(searchVal);
        }
        
        dispatch(replaceFilters(newFilters));
        dispatch(fetchExploreUsers({ filters: newFilters }));
    }
  }, [seoCity, seoCategory, searchParams]);

  // Initial Fetch
  useEffect(() => {
    dispatch(fetchFilterOptions());
    dispatch(fetchFilterPresets());
    dispatch(fetchFilterHistory());

    // Only fetch if no SEO params and no search params
    if (!seoCity && !seoCategory && Object.keys(searchParamFilters).length === 0) {
      dispatch(fetchExploreUsers({ filters }));
    }
  }, [dispatch]);

  // Handlers
  const handleFilterChange = useCallback((key: keyof ExploreFilters, value: any) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    dispatch(setFilters(newFilters));
    dispatch(fetchExploreUsers({ filters: newFilters }));
  }, [dispatch, filters]);

  const handleSearchChange = (query: string) => {
    setSearchInput(query);
    // Debounce could be added here if needed, but for now we'll search on enter or delay
    const timeoutId = setTimeout(() => {
      handleFilterChange('search', query);
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  const clearAllFilters = () => {
    dispatch(clearFilters());
    setSearchInput('');
    dispatch(fetchExploreUsers({ filters: { page: 1, limit: 20 } }));
  };

  const loadMore = () => {
    if (hasMore && !loading && pagination && pagination.current_page) {
      const nextPage = pagination.current_page + 1;
      dispatch(fetchExploreUsers({ 
        filters: { ...filters, page: nextPage },
        loadMore: true 
      }));
    }
  };

  const handleNearbySearch = async () => {
    setNearbyLoading(true);
    try {
      await getCurrentLocation();
      const coords = await locationAPI.getCurrentLocation();
      
      const locationName = coords.name ? coords.name : "Current Location";
      const nearbyFilters = {
        ...filters,
        location: `Near ${locationName}`,
        latitude: coords.latitude,
        longitude: coords.longitude,
        radius: 50,
        page: 1
      };
      
      dispatch(setFilters(nearbyFilters));
      dispatch(fetchExploreUsers({ filters: { ...nearbyFilters, page: 1 } }));
      toast.success(`Showing users near ${locationName}`);
    } catch (error: any) {
      console.error('Nearby search error:', error);
      toast.error('Failed to get location. Please enable location access.');
    } finally {
      setNearbyLoading(false);
    }
  };

  const handleFollowToggle = async (user: User) => {
    if (!isAuthenticated || !currentUser) {
      setLoginModalMessage('Sign in to follow and connect with creators');
      setShowLoginModal(true);
      return;
    }

    setFollowLoading(true);
    try {
      const response = await userRelationshipAPI.toggleFollow(user.id);
      const following = response.data.data.following;

      if (response.data.success) {
        dispatch(updateUser({
          following_count: (currentUser.following_count || 0) + (following ? 1 : -1),
        }));

        setFollowMap(prev => ({
          ...prev,
          [user.id]: following
        }));
        
        toast.success(following ? `Following ${user.first_name}` : `Unfollowed ${user.first_name}`);
      }
    } catch (error: any) {
      console.error('Failed to toggle follow:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update follow status';
      toast.error(errorMessage);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleMessageUser = (targetUser: User) => {
    if (!isAuthenticated) {
      setLoginModalMessage('Sign in to message this creator');
      setShowLoginModal(true);
      return;
    }
    navigate('/messages', {
      state: {
        openConversationWith: targetUser.id,
        userName: `${targetUser.first_name} ${targetUser.last_name}`,
        userAvatar: targetUser.profile_pic,
        userType: targetUser.user_type,
      }
    });
  };

  const handleCollaborate = (user: User) => {
    if (!isAuthenticated) {
      setLoginModalMessage('Sign in to collaborate with creators');
      setShowLoginModal(true);
      return;
    }
    // [Subscription removed] Collaboration request creation is unrestricted.
    setSelectedUserForCollab(user);
    setShowCollaborationModal(true);
  };

  const activeFiltersCount = Object.keys(filters).filter(key => 
    key !== 'page' && key !== 'limit' && key !== 'sort_by' && key !== 'sort_order' && filters[key as keyof ExploreFilters]
  ).length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Public Navigation for non-authenticated users */}
      {/* Public Navigation for non-authenticated users */}
      {!isAuthenticated && <GuestHeader />}
      
      <div className={!isAuthenticated ? 'pt-16' : ''}>
        <SEOHead
        title={
          seoCity && seoCategory 
            ? `${seoCategory.name} in ${seoCity.name} | ClickCollab`
            : seoCity 
              ? `Photographers & Models in ${seoCity.name} | ClickCollab`
              : seoCategory 
                ? `${seoCategory.name} | ClickCollab`
                : 'Explore Photographers & Models Near You'
        }
        description={
          seoCity && seoCategory 
            ? `Find the best ${seoCategory.name.toLowerCase()} in ${seoCity.name}. ${seoCategory.description}. Browse portfolios, check reviews, and book directly on ClickCollab.`
            : seoCity 
              ? `Discover talented photographers, videographers, and models in ${seoCity.name}. Browse portfolios and connect with creative professionals near you.`
              : seoCategory 
                ? `${seoCategory.description}. Find verified professionals, view portfolios, and book on ClickCollab.`
                : 'Discover and connect with talented photographers, videographers, and models on ClickCollab. Search by location, browse portfolios, filter by budget, and collaborate with creative professionals in your area.'
        }
        noindex={!loading && filteredUsers.length === 0}
        keywords={[
          // Primary - High Volume
          'photographer near me',
          'photographers near me',
          'hire photographer India',
          'find photographer',
          'model near me',
          'models near me',
          // Wedding - Very High Volume
          'wedding photographer near me',
          'candid wedding photographer',
          'pre-wedding photographer',
          'engagement photographer',
          'wedding videographer',
          // Commercial
          'product photographer',
          'event photographer',
          'birthday photographer',
          'commercial photographer',
          'corporate photographer',
          // Portrait & Fashion
          'portrait photographer',
          'fashion photographer',
          'headshot photographer',
          'lifestyle photographer',
          // Models
          'fashion model',
          'commercial model',
          'fitness model',
          // Platform & Intent
          'book photographer online',
          'hire model for photoshoot',
          'photography platform India',
          'photographer directory',
          'model booking platform',
          // Location-based (dynamic)
          ...(seoCity ? [`photographer ${seoCity.name}`, `model ${seoCity.name}`, `${seoCity.name} photographer`] : []),
          ...(seoCategory ? [seoCategory.slug.replace(/-/g, ' '), `best ${seoCategory.slug.replace(/-/g, ' ')}`] : []),
        ]}
        url={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <div className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <ExploreHeader 
          searchQuery={searchInput}
          onSearchChange={handleSearchChange}
          onNearbyClick={handleNearbySearch}
          isNearbyLoading={nearbyLoading}
        />

        <FilterBar 
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearAllFilters}
          onShowAdvancedFilters={() => setShowFilters(true)}
          activeFiltersCount={activeFiltersCount}
        />
      </div>

      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        {/* Results Grid */}
        {filteredUsers.length > 0 ? (
          <div className={`grid gap-4 md:gap-5 ${
            !isAuthenticated 
              ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
              : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3'
          }`}>
            <AnimatePresence mode='popLayout'>
              {filteredUsers.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  currentUser={currentUser}
                  onFollowToggle={handleFollowToggle}
                  onMessage={handleMessageUser}
                  onCollaborate={handleCollaborate}
                  onQuickView={(usr) => {
                    setSelectedUserForQuickView(usr);
                    setShowQuickViewModal(true);
                  }}
                  isFollowing={followMap[user.id] ?? user.do_i_follow}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          !loading && (
            <div className="text-center py-20">
              <div className="bg-white dark:bg-gray-800 p-8 rounded-full inline-block mb-4 shadow-sm">
                <FaceFrownIcon className="w-16 h-16 text-gray-300 dark:text-gray-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No users found</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                We couldn't find any users matching your criteria. Try adjusting your filters or search terms.
              </p>
              <Button onClick={clearAllFilters} variant="outline">
                Clear all filters
              </Button>
            </div>
          )
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
          </div>
        )}

        {/* Infinite Scroll Sentinel */}
        <div ref={sentinelRef} className="h-10 w-full" />
        
        {/* Loading Indicator for Infinite Scroll */}
        {loading && filteredUsers.length > 0 && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        )}
      </main>

      {/* Collaboration Modal */}
      {selectedUserForCollab && (
        <CollaborationRequestModal
          isOpen={showCollaborationModal}
          onClose={() => {
            setShowCollaborationModal(false);
            setSelectedUserForCollab(null);
          }}
          receiver={selectedUserForCollab}
        />
      )}
      
      <FilterModal 
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        onApplyFilters={(newFilters) => {
          dispatch(setFilters({ ...newFilters, page: 1 }));
          dispatch(fetchExploreUsers({ filters: { ...newFilters, page: 1 } }));
          setShowFilters(false);
        }}
        onClearFilters={clearAllFilters}
        specializationOptions={filterOptions?.specializations || []}
      />

      {/* Login Prompt Modal for Guests */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message={loginModalMessage}
      />

      {showQuickViewModal && selectedUserForQuickView && (
        <ModelQuickViewModal
          isOpen={showQuickViewModal}
          onClose={() => {
            setShowQuickViewModal(false);
            setSelectedUserForQuickView(null);
          }}
          user={selectedUserForQuickView}
          onBook={() => {
            setShowQuickViewModal(false);
            handleCollaborate(selectedUserForQuickView);
          }}
          onViewProfile={() => {
            setShowQuickViewModal(false);
            navigate(`/profile/${selectedUserForQuickView.id}`);
          }}
        />
      )}

      {/* [Subscription removed] UpgradePopup removed */}
      </div>
    </div>
  );
};

export default ExplorePage;
